import type { ClerkProviderProps } from '@clerk/clerk-react';

// Restyles Clerk's SignIn to the handoff's signed-out screen: bare card on the
// surface column, outline social buttons, mono field labels, gold Continue.
// Colours are theme tokens so the form follows the OS colour scheme with the
// rest of the page.
export const clerkAppearance: ClerkProviderProps['appearance'] = {
  variables: {
    colorPrimary: 'var(--accFill)',
    colorPrimaryForeground: 'var(--accOn)',
    colorBackground: 'var(--surface)',
    colorInput: 'var(--bg)',
    colorInputForeground: 'var(--ink)',
    colorForeground: 'var(--ink)',
    colorMutedForeground: 'var(--mute)',
    colorMuted: 'var(--stripeA)',
    colorBorder: 'var(--line2)',
    colorRing: 'var(--accFill)',
    colorNeutral: 'var(--ink)',
    colorDanger: 'var(--down)',
    colorSuccess: 'var(--up)',
    colorWarning: 'var(--acc)',
    borderRadius: '6px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '14px',
    spacing: '1.05rem',
  },
  elements: {
    rootBox: 'w-full',
    cardBox: 'w-full max-w-none shadow-none rounded-none',
    // Clerk offsets the card by -1px for a border this theme removes; without
    // the reset the card box clips the first letter of every field label.
    card: 'bg-transparent shadow-none border-0 !m-0 p-0 gap-0',
    logoBox: 'hidden',
    header: 'text-left gap-0',
    headerTitle: 'text-[26px] font-semibold tracking-[-0.025em] text-ink',
    headerSubtitle: 'hidden',
    main: 'mt-7 gap-0',
    socialButtons: 'flex flex-col gap-3',
    socialButtonsBlockButton:
      'h-auto justify-start rounded-md !border !border-solid !border-line2 bg-transparent px-4 py-[13px] text-sm font-semibold text-ink !shadow-none hover:bg-transparent hover:text-acc',
    socialButtonsBlockButtonText: 'text-left text-sm font-semibold',
    dividerRow: 'my-[26px] gap-[14px]',
    dividerLine: 'bg-line',
    dividerText: 'font-mono text-[10px] uppercase tracking-[0.14em] text-faint',
    form: 'gap-0',
    formFieldRow: 'gap-0',
    formFieldLabelRow: 'mb-2',
    formFieldLabel: 'font-mono text-[10px] uppercase tracking-[0.14em] text-mute',
    formFieldInput:
      'h-11 rounded-md !border !border-solid !border-line bg-bg px-[13px] text-[15px] text-ink !shadow-none focus:!border-line2 focus:ring-0',
    formButtonPrimary:
      'mt-4 h-auto rounded-md border-0 bg-accFill px-[18px] py-[13px] text-sm font-semibold text-accOn !shadow-none hover:bg-accFill hover:opacity-90',
    buttonArrowIcon: 'hidden',
    otpCodeFieldInput: '!border !border-solid !border-line bg-bg text-ink !shadow-none',
    // The consent checkbox Clerk adds to sign-up once the instance requires
    // legal acceptance: its label reads like the sign-in line under the card,
    // with the two documents underlined.
    formFieldCheckboxInput: 'accent-accFill',
    formFieldCheckboxLabel:
      'text-[13px] leading-[1.6] text-mute [&_a]:text-ink [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-acc',
    footer: 'hidden',
    footerAction: 'hidden',
    footerPages: 'hidden',
  },
};

// Clerk copy in the product's words. The combined sign-in-or-up flow reads the
// *Combined key for its title. Sign-up runs in Clerk's waitlist mode, so the
// refusal a visitor without an invitation meets and the waitlist card it sends
// them to both say the beta is invite-only, in step with the marketing site.
// The consent checkbox, shown once the instance requires legal acceptance,
// names the documents the way the line under the card does. Later steps keep
// Clerk's own copy.
export const clerkLocalization: ClerkProviderProps['localization'] = {
  signIn: {
    start: {
      title: 'Sign in',
      titleCombined: 'Sign in',
    },
  },
  signUp: {
    restrictedAccess: {
      title: 'The beta is invite-only',
      subtitleWaitlist: "Join the waitlist and we'll send your invite the moment doors open.",
      blockButton__joinWaitlist: 'Join the waitlist',
    },
    legalConsent: {
      checkbox: {
        label__termsOfServiceAndPrivacyPolicy:
          'I agree to the {{ termsOfServiceLink || link("terms") }} and the {{ privacyPolicyLink || link("privacy policy") }}.',
      },
    },
  },
  waitlist: {
    start: {
      title: 'Join the waitlist',
      subtitle: "The beta is invite-only. Leave your email and we'll send your invite the moment doors open.",
      formButton: 'Join the waitlist',
    },
    success: {
      title: "You're on the list",
      subtitle: "We'll email your invite the moment doors open.",
    },
  },
};
